import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Organization, switchMap, forkJoin, ImageUtil, of, OrganizationPast } from '../store';
import { HttpService } from './http.service';
import { map } from 'rxjs/operators/map';

import * as lodash from 'lodash';
import { catchError } from 'rxjs/operators';
import { HttpHeaders } from '@angular/common/http';

@Injectable()
export class AssociationRegistrationService {

  private _links = [];
  private _mainLink = { title: 'Assocation', url: '/association' };

  private _api = '/api/association';
  private _url = '/api/ClubProgram';
  private _collegeScope = '/api/CollegeScope';

  constructor(
    private httpService: HttpService
  ) { }

  getOrgListByClubID(clubId: number): any {
    return this.httpService.getRaw(`${this._api}/get-org-governing-body-list/${clubId}`).pipe(
      switchMap((res: any) => forkJoin(res.map(r => this.getOrgList(r.orgID, clubId)))),
      map((res: any) => res.reduce((arr: Organization[], c: Organization[]) => arr.concat(c)))
    );
  }

  getOrgList(parentOrgId: number, clubID: number): Observable<Organization[]> {
    return this.httpService.getRaw(`${this._api}/get-child-org-list-new/${parentOrgId}/${clubID}`).pipe(map((res: any) => res.map(c => new Organization(c))));
  }

  getOrg(orgId: number, seasonId: number): Observable<Organization> {
    return this.httpService.get(`${this._api}/get-child-org/${orgId}/${seasonId}`);
  }

  getTeam(orgId: number, clubId: number, seasonId: number, teamId: number): Observable<Organization> {
    return this.httpService.get(`${this._api}/get-org-club-team-by-id/${orgId}/${clubId}/${seasonId}/${teamId}`);
  }

  getTeamsList(clubID: number, orgID: number, orgSeasonID: number, status: number = 0) {
    return this.httpService.get(`${this._api}/get-org-club-team-list/${status}/${orgID}/${clubID}/${orgSeasonID}`);
  }

  // Players
  getAllPlayers(orgID: number, clubID: number, orgSeasonID: number, status: number = 0) {
    return this.httpService.get(`${this._api}/get-registered-player-list/${status}/${orgID}/${clubID}/${orgSeasonID}`)
      .pipe(map(this.updateDates));
  }

  getAllPlayersCount(orgID: number, clubID: number, orgSeasonID: number) {
    return this.httpService.getRaw(`${this._api}/get-registered-player-counts/${orgID}/${clubID}/${orgSeasonID}`);
  }

  getTeamPlayersCount(orgID: number, teamID: number, orgSeasonID: number, divisionID: number, clubID: number) {
    return this.httpService.get(`${this._api}/get-team-player-counts/${orgID}/${teamID}/${orgSeasonID}/${divisionID}/${clubID}`);
  }

  getTeamPlayersList(status: number, orgID: number, teamID: number, orgSeasonID: number, divisionID: number, clubID: number) {
    return this.httpService.get(`${this._api}/get-team-player-list/${status}/${orgID}/${teamID}/${orgSeasonID}/${divisionID}/${clubID}`)
      .pipe(map(this.updateDates));
  }

  getDeniedPlayerInfo(orgId: number, seasonId: number, userId: number, teamId: number) {
    return this.httpService.get(`${this._api}/get-denied-player-data/${orgId}/${seasonId}/${userId}/${teamId}`);
  }

  registerPlayer(data, isStaff = false) {
    return this.httpService.postRaw(`${this._api}/${isStaff ? 'register-staff-payment-new' : 'register-player-payment-new'}`, data);
  }

  registerStaffWithZeroFee(clubID: number, orgID: number, orgSeasonID: number, clubDirectorID: number, staffData) {
    return this.httpService.postRaw(`${this._api}/register-staff-with-zero-fee/${clubID}/${orgID}/${orgSeasonID}/${clubDirectorID}`, staffData);
  }

  // Update Player
  saveMissingInfo(data) {
    return this.httpService.postRaw(`${this._api}/save-missing-player-info`, data, { responseType: 'text' });
  }

  // Print Card
  printCard(data, isStaff = true) {
    return this.httpService.postRaw(`${this._api}/print-card/` + (isStaff ? 'staff' : 'player'), data, { responseType: 'text' });
  }

  fixDeniedPlayer(data) {
    return this.httpService.post(`${this._api}/fix-denied-player`, data, { responseType: 'Text' });
  }

  uploadForms(playerId: number, files) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('file', file);
      formData.append('fileName', file.name);
    });
    return this.httpService.post(`${this._api}/UploadFiles/${playerId}`, formData);
  }

  // Staff
  getStaff(status: number, orgId: number, seasonID: number, clubID: number, teamId: number = -1, roleId: number = -1) {
    return this.httpService.get(`${this._api}/get-team-staff-list/${status}/${orgId}/${seasonID}/${clubID}/${teamId}/${roleId}`)
      .pipe(map(this.updateDates));
  }

  getStaffCounts(orgId: number, seasonID: number, clubID: number, teamId: number = -1, roleId: number = -1) {
    return this.httpService.get(`${this._api}/get-team-staff-counts/${orgId}/${seasonID}/${clubID}/${teamId}/${roleId}`);
  }

  getAllStaff(status: number, orgId: number, seasonID: number, clubID: number) {
    return this.httpService.get(`${this._api}/get-registered-staff-list/${status}/${orgId}/${clubID}/${seasonID}`)
      .pipe(map(this.updateDates));
  }

  getAllStaffCounts(orgId: number, seasonID: number, clubID: number) {
    return this.httpService.get(`${this._api}/get-registered-staff-counts/${orgId}/${clubID}/${seasonID}`);
  }

  getDeniedStaffInfo(userId: number, orgId: number, seasonId: number) {
    return this.httpService.get(`${this._api}/get-denied-staff-data/${userId}/${orgId}/${seasonId}`);
  }

  fixDeniedStaff(data) {
    return this.httpService.postRaw(`${this._api}/fix-denied-staff`, data, { responseType: 'text' });
  }

  // Reports
  getReportsData(orgId: number, seasonId: number, clubId: number) {
    return this.httpService.get(`${this._api}/get-association-reports-data/${orgId}/${seasonId}/${clubId}`)
      .pipe(map(res => lodash.sortBy(res, 'paymentDate').reverse()));
  }

  getExportReportData(orgID: number, orgSeasonID: number, clubID: number, transactionID: string) {
    return this.httpService.get(`${this._api}/get-association-reports-receipt/${orgID}/${orgSeasonID}/${clubID}/${transactionID}`).pipe(
      map(res => lodash.sortBy(res, (a) => new Date('paymentDate').getTime()).reverse())
    );
  }

  // Landing
  getStatsForAllOrgs(clubId: number) {
    return this.httpService.getRaw(`${this._api}/get-registered-player-counts-all/${clubId}`);
  }

  getFiles(images: string[]) {
    return this.httpService.post(`${this._api}/profile-images`, images);
  }

  async getFileContent(urls: string[]) {
    // const blobs = await forkJoin(urls.map(url =>
    //   this.httpService.getRaw(url + '?' + new Date().getTime(), { responseType: 'arrayBuffer' }).pipe(
    //     map((res: any) => new Blob(res)),
    //     catchError(err => of(''))
    //   )
    // )).toPromise();
    // return Promise.all(blobs.map(blob => blob ? ImageUtil.createImageFromBlob(blob) : Promise.resolve('')));
    return Promise.all(urls.map(url => ImageUtil.convertImgToBase641(url + '?' + new Date().getTime())));
  }

  checkChildClub(clubId: number) {
    return this.httpService.get(`${this._api}/is-org-club/${clubId}`);
  }

  checkClubSportIsBasketball(clubId: number) {
    return this.httpService.get(`${this._url}/check-club-sport-is-basketball/${clubId}`);
  }

  get links() {
    return [this._mainLink, ...this._links];
  }

  set links(links) {
    this._links = links;
  }

  addLinks(links) {
    this._links = this._links.concat(links);
  }

  updateDates(res) {
    res.forEach(r => {
      Object.keys(r).forEach(k => {
        if (r[k] === '0001-01-01T00:00:00') {
          r[k] = null;
        }
      });
    });

    return res;
  }

  /* Staff Page */
  getStaffMembersListByClubID(clubID: number, roleID: number = 0) {
    return this.httpService.get(`${this._api}/get-staffs/${clubID}`, { params: { roleID } });
  }

  getStaffMembersListByTeamID(teamID: number) {
    return this.httpService.get(`${this._api}/get-team-staff-list/${teamID}`);
  }

  getStaffInfoData(userID: number, orgID: number, orgSeasonID: number) {
    return this.httpService.get(
      `${this._api}/get-org-staff-info/${userID}/${orgID}/${orgSeasonID}`
    );
  }

  getStaffBasicInfo(userID: number) {
    return this.httpService.get(
      `${this._api}/get-org-staff-basic-info/${userID}`
    );
  }

  getAllCountries() {
    return this.httpService.get(`${this._api}/get-all-countries/`);
  }

  getAllSates() {
    return this.httpService.get(`${this._api}/get-all-states/`);
  }

  updateOrgPlayerBasicInfo(payload: any) {
    return this.httpService.post(`${this._api}/update-org-player-basic-info`, payload);
  }

  updateOrgStaffBasicInfo(payload: any) {
    return this.httpService.post(`${this._api}/update-org-staff-basic-info`, payload);
  }

  getUserProgramRoles() {
    return this.httpService.get(`${this._api}/get-user-program-roles/`);
  }

  getUserPermission(clubID: number, userID: number) {
    return this.httpService.get(`${this._api}/get-user-permission/${clubID}/${userID}`);
  }

  updateUserPermission(userID: number, data) {
    return this.httpService.post(`${this._api}/update-user-permission/${userID}`, data);
  }

  printTeamOfficialRosters(payload) {
    return this.httpService.postRaw(`${this._api}/print-org-team-official-rosters`, payload);
  }

  printTeamOfficialRostersWithoutS3(payload) {
    return this.httpService.postRaw(`${this._api}/print-org-team-official-rosters-without-s3`, payload);
  }

  printClubAssociationRecieptByChargeID(orgID: number, orgSeasonID: number, clubID: number, transactionID: string) {
    return this.httpService.postRaw(`${this._api}/print-club-association-reciept-by-chargeID/${orgID}/${orgSeasonID}/${clubID}/${transactionID}`);
  }

  getPendingTransferPlayerList(clubID: number, teamId: number, orgSeasonID: number) {
    return this.httpService.get(`${this._api}/get-pending-transfer-player-list/${clubID}/${teamId}/${orgSeasonID}`);
  }
  updateTransferDocument(tranferObj: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        "Content-Type": "application/json"
      })
    };
    return this.httpService.post(this._api + '/update-transfer-document', tranferObj, httpOptions);
  }
  getPastSeasons(orgID: number) {
    return this.httpService.get(`${this._api}/get-org-past-seasons/${orgID}`);
  }

  /*
   getOrgListByClubID(clubId: number): any {
    return this.httpService.getRaw(`${this._api}/get-org-governing-body-list/${clubId}`).pipe(
      switchMap((res: any) => forkJoin(res.map(r => this.getOrgList(r.orgID, clubId)))),
      map((res: any) => res.reduce((arr: Organization[], c: Organization[]) => arr.concat(c)))
    );
  }

  getOrgList(parentOrgId: number, clubID: number): Observable<Organization[]> {
    return this.httpService.getRaw(`${this._api}/get-child-org-list-new/${parentOrgId}/${clubID}`).pipe(map((res: any) => res.map(c => new Organization(c))));
  }
   */


  getPastSeasonChildOrgList(orgSeasonID: number, clubID: number): Observable<Organization[]> {
    return this.httpService.get(`${this._api}/get-past-season-child-org-list/${orgSeasonID}/${clubID}`).pipe(map((res: any) => res.map(c => new Organization(c))));
  }

  getOrgGoverningBodyListByOrgSeasonIDAndClubID(orgSeasonID: number, clubID: number) {
    return this.httpService.getRaw(`${this._api}/get-org-governing-body-list-by-orgSeasonID-and-clubID/${orgSeasonID}/${clubID}`).pipe(
      switchMap((res: any) => forkJoin(res.map(r => this.getPastSeasonChildOrgList(orgSeasonID, clubID)))),
      map((res: any) => res.reduce((arr: OrganizationPast[], c: OrganizationPast[]) => arr.concat(c)))
    );
    
  }
  getStatsForAllPastOrgs(clubId: number, orgSeasonID:number) {
    return this.httpService.get(`${this._api}/get-registered-players-and-staff-counts-all-org/${orgSeasonID}/${clubId}`);
  }

  updateTgsStaffBasicInfo(data) {
    return this.httpService.postRaw(`${this._collegeScope}/update-tgs-staff-basic-info`, data);
  }
}



// WEBPACK FOOTER //
// ./src/app/services/association-registration.service.ts